import React, { useState, useEffect } from "react";
import { ref, communication as commApi, officeVisitors as visitorsApi } from "../services/api";
import toast from "react-hot-toast";
import { FiPlus, FiTrash2, FiEdit2, FiCheck, FiX, FiFileText, FiSave, FiEye, FiCode, FiInfo, FiMessageSquare } from "react-icons/fi";
// v3.3.970 — ربط واتساب انتقل من تبويبات هذه الصفحة إلى القائمة الجانبية

const CATEGORIES = [
  { key: "_header_res",           label: "نظام الحجوزات", header: true },
  { key: "operation_type",        label: "مصدر الحجز" },
  { key: "purchase_purpose",      label: "هدف الشراء" },
  { key: "unit_type",             label: "نوع الوحدة" },
  { key: "readiness",             label: "نوع جاهزية الوحدة" },
  { key: "deposit_status",        label: "حالة العربون", hasColor: true },
  { key: "bank_account",          label: "الحساب المحول له" },
  { key: "operation_status",      label: "حالة الحجز", hasColor: true },
  { key: "commission_status_res", label: "حالة الدلالة (إدارة)", hasColor: true },
  { key: "commission_status_acc", label: "حالة الدلالة (محاسبة)" },
  { key: "commission_split_preset", label: "أنواع توزيع الإدارة والشركة", hasSplit: true }, // v3.4.127 — الاسم + نسب (الإدارة/المبيعات/المسوقات/الشركة) تُخزن في group_name
  { key: "internal_split_preset", label: "أنواع توزيع الحصة الداخلية (المسوّقون)", hasSplit: true, splitKeys: [["m1", "المسوّق الداخلي 1"], ["m2", "المسوّق الداخلي 2"], ["dalilak", "دليلك"]], splitMax: true }, // v3.4.133 — الباقي للإدارة والشركة
  { key: "_header_client",        label: "العملاء", header: true },
  { key: "client_type",           label: "مصدر العميل" },
  { key: "client_category",       label: "نوع العميل" },
  { key: "client_stage",          label: "تصنيف العميل" },
  { key: "client_interests_districts",     label: "اهتمامات: الأحياء" },
  { key: "client_interests_project_types", label: "اهتمامات: نوع المشاريع" },
  { key: "client_interests_unit_types",    label: "اهتمامات: نوع الوحدات" },
  { key: "_header_proj",          label: "المشاريع", header: true },
  { key: "resale_filter_method",  label: "طريقة التصفية (إعادة البيع)" },
  { key: "unit_facade",           label: "الواجهة" },
  { key: "unit_status_custom",    label: "حالة الوحدة" },
  { key: "_header_mkt",           label: "المسوقين", header: true },
  { key: "marketer_interests",    label: "اهتمامات المسوق (Tags)" },
  // v3.3.782 — قوائم فورم البوابة الخارجية (تسجيل العملاء المهتمين)
  { key: "_header_portal",         label: "البوابة الخارجية", header: true },
  { key: "portal_client_category", label: "نوع العميل (البوابة)" },
  { key: "portal_source",          label: "مصدر العميل (البوابة)" },
  { key: "portal_interest",        label: "اهتمام العميل (البوابة)", hasGroup: true }, // v3.3.784 — مجموعات (بوكسات بالفورم)
];

// المتغيرات المتاحة للعقود
const CONTRACT_VARIABLES = [
  { key: "{{Day}}", label: "اليوم", source: "تاريخ العقد", example: "الأحد" },
  { key: "{{Date}}", label: "التاريخ", source: "تاريخ العقد", example: "2026/03/29" },
  { key: "{{Name}}", label: "اسم العميل", source: "بيانات العميل", example: "محمد أحمد" },
  { key: "{{National_ID}}", label: "رقم الهوية", source: "بيانات العميل", example: "1234567890" },
  { key: "{{Second_Mobile}}", label: "رقم الجوال", source: "بيانات العميل", example: "0501234567" },
  { key: "{{Lot#}}", label: "رقم القطعة", source: "بيانات المشروع", example: "15" },
  { key: "{{FlatType#}}", label: "رقم الشقة", source: "بيانات الوحدة", example: "A2-8" },
  { key: "{{Floor#}}", label: "الدور", source: "بيانات الوحدة", example: "8" },
  { key: "{{Front#}}", label: "الواجهة", source: "بيانات الوحدة", example: "شمالية" },
  { key: "{{Area#}}", label: "المساحة", source: "بيانات الوحدة", example: "150" },
  { key: "{{Rooms#}}", label: "عدد الغرف", source: "بيانات الوحدة", example: "3" },
  { key: "{{RestR#}}", label: "دورات المياه", source: "بيانات الوحدة", example: "2" },
  { key: "{{LivingR#}}", label: "الصالة", source: "بيانات الوحدة", example: "1" },
  { key: "{{Kitchen#}}", label: "المطبخ", source: "بيانات الوحدة", example: "1" },
  { key: "{{Maid#}}", label: "غرفة خادمة", source: "بيانات الوحدة", example: "لا يوجد" },
  { key: "{{#Price}}", label: "السعر (رقم)", source: "بيانات الحجز", example: "500,000" },
  { key: "{{#PriceR}}", label: "السعر (كتابة)", source: "بيانات الحجز", example: "خمسمائة ألف" },
  { key: "{{#PaymentOption}}", label: "طريقة الدفع", source: "بيانات الحجز", example: "كاش" },
  { key: "{{#Plenty}}", label: "الغرامة", source: "بيانات العقد", example: "10%" },
];

const PRESET_COLORS = ["#22c55e","#ef4444","#f59e0b","#3b82f6","#8b5cf6","#ec4899","#06b6d4","#84cc16","#f97316","#6366f1","#14b8a6","#94a3b8"];

const API_BASE = (typeof window !== "undefined" ? window.location.origin : ""); // v3.4.482 — نفس العنوان المفتوح (نطاق DO أو erp.rael.com.sa)

export default function SettingsPage() {
  const [mainTab, setMainTab] = useState("options"); // options | templates
  const [allOptions, setAllOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("operation_type");
  const [newLabel, setNewLabel] = useState("");
  const [newColor, setNewColor] = useState("#3b82f6");
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editColor, setEditColor] = useState("");
  // v3.3.784 — مجموعة الخيار (لقوائم hasGroup مثل اهتمام البوابة)
  const [newGroup, setNewGroup] = useState("");
  const [editGroup, setEditGroup] = useState("");

  const currentCatConfig = CATEGORIES.find(c => c.key === activeCat);
  const hasColor = currentCatConfig?.hasColor;
  const hasGroup = currentCatConfig?.hasGroup;
  // v3.4.127 — أنواع التوزيع: 4 نسب مجموعها 100 تُحفظ في group_name بصيغة "الإدارة/المبيعات/المسوقات/الشركة"
  const hasSplit = currentCatConfig?.hasSplit;
  const SPLIT_KEYS = currentCatConfig?.splitKeys || [["waha", "الإدارة"], ["sales", "مدير المبيعات"], ["mkt", "مديرة المسوقات"], ["company", "الشركة"]]; // v3.4.133 — مفاتيح حسب الفئة
  const splitOk = (sum) => (currentCatConfig?.splitMax ? sum <= 100.01 : Math.abs(sum - 100) < 0.01); // v3.4.133 — الداخلي: ≤ 100 (الباقي للإدارة والشركة)
  const [newSplit, setNewSplit] = useState({});
  const [editSplit, setEditSplit] = useState({});
  const splitToSpec = (sp) => SPLIT_KEYS.map(([k]) => Number(sp[k]) || 0).join("/");
  const specToSplit = (spec) => { const a = String(spec || "").split("/"); const o = {}; SPLIT_KEYS.forEach(([k], i) => { o[k] = a[i] || ""; }); return o; };
  const splitSum = (sp) => SPLIT_KEYS.reduce((s, [k]) => s + (Number(sp[k]) || 0), 0);
  const SplitInputs = ({ sp, setSp }) => (
    <div style={{ display: "flex", gap: 6, alignItems: "flex-end", flexWrap: "wrap" }}>
      {SPLIT_KEYS.map(([k, l]) => (
        <div key={k} style={{ width: 92 }}>
          <label style={{ fontSize: 10, color: "#64748b" }}>% {l}</label>
          <input type="number" min={0} max={100} value={sp[k]} onChange={e => setSp({ ...sp, [k]: e.target.value })}
            style={{ width: "100%", padding: "6px 8px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 12.5, marginTop: 2, boxSizing: "border-box", direction: "ltr" }} />
        </div>
      ))}
      <span style={{ fontSize: 11, fontWeight: 800, color: splitOk(splitSum(sp)) ? "#166534" : "#b91c1c", paddingBottom: 8 }}>= {splitSum(sp)}%{splitOk(splitSum(sp)) ? (currentCatConfig?.splitMax ? ` ✓ (الباقي للإدارة والشركة: ${Math.round((100 - splitSum(sp)) * 10) / 10}%)` : " ✓") : (currentCatConfig?.splitMax ? " (لا يتجاوز 100)" : " (يجب 100)")}</span>
    </div>
  );

  const fetchOptions = () => {
    ref.getAllOptions()
      .then(r => setAllOptions(r.data.options))
      .catch(() => toast.error("فشل التحميل"))
      .finally(() => setLoading(false));
  };

  useEffect(fetchOptions, []);
  const notifyRefs = () => window.dispatchEvent(new Event("rael:refs-changed")); // v3.4.134 — تحديث كاش المراجع (RefProvider) فورًا بعد أي تعديل هنا حتى تظهر التغييرات في نموذج الحجز دون إعادة تحميل الصفحة

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    if (hasSplit && !splitOk(splitSum(newSplit))) return toast.error(currentCatConfig?.splitMax ? "مجموع النسب لا يتجاوز 100%" : "مجموع النسب يجب أن يكون 100%"); // v3.4.127 / v3.4.133
    try {
      await ref.addOption(activeCat, newLabel.trim(), hasColor ? newColor : "", hasSplit ? splitToSpec(newSplit) : (hasGroup ? newGroup.trim() : undefined));
      setNewLabel(""); setNewColor("#3b82f6"); // v3.3.784 — تبقى المجموعة كما هي لإضافات متتالية بنفس المجموعة
      toast.success("تمت الإضافة");
      fetchOptions(); notifyRefs();
    } catch (err) { toast.error("فشل"); }
  };

  const handleUpdate = async (id) => {
    if (!editLabel.trim()) return;
    try {
      const payload = { label: editLabel.trim(), color: editColor };
      if (hasGroup) payload.group_name = editGroup.trim(); // v3.3.784
      if (hasSplit) { if (!splitOk(splitSum(editSplit))) return toast.error(currentCatConfig?.splitMax ? "مجموع النسب لا يتجاوز 100%" : "مجموع النسب يجب أن يكون 100%"); payload.group_name = splitToSpec(editSplit); } // v3.4.127 / v3.4.133
      await ref.updateOption(id, payload);
      setEditingId(null);
      toast.success("تم التحديث");
      fetchOptions(); notifyRefs();
    } catch (err) { toast.error("فشل"); }
  };

  const handleColorChange = async (id, color) => {
    try {
      await ref.updateOption(id, { color });
      fetchOptions(); notifyRefs();
    } catch (err) { toast.error("فشل"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("حذف؟")) return;
    try { await ref.deleteOption(id); toast.success("تم"); fetchOptions(); notifyRefs(); } catch (err) { toast.error("فشل"); }
  };

  const handleToggle = async (item) => {
    try { await ref.updateOption(item.id, { is_active: !item.is_active }); fetchOptions(); } catch (err) { toast.error("فشل"); }
  };

  if (loading) return <div style={{ padding: 40, color: "#64748b" }}>جاري التحميل...</div>;

  // v3.3.784 — في قوائم المجموعات: ترتيب العرض مجموعة بعد مجموعة
  // v3.3.787 — الترتيب الفعلي عبر sort_order (أسهم ↑↓ للمجموعات والخيارات)
  const items = (() => {
    const base = allOptions[activeCat] || [];
    if (!hasGroup) return base;
    return [...base].sort((a, b) =>
      (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) ||
      String(a.label).localeCompare(String(b.label), "ar"));
  })();

  // v3.3.787 — أقسام المجموعات بترتيب أول ظهور (يتبع sort_order)
  const sections = (() => {
    if (!hasGroup) return [{ name: null, items }];
    const order = [];
    const map = {};
    items.forEach(it => {
      const g = it.group_name || "بدون مجموعة";
      if (!map[g]) { map[g] = []; order.push(g); }
      map[g].push(it);
    });
    return order.map(g => ({ name: g, items: map[g] }));
  })();

  // v3.3.787 — إعادة ترقيم sort_order حسب الترتيب الجديد ثم تحديث
  const renumber = async (ordered) => {
    try {
      await Promise.all(ordered.map((it, idx) =>
        (Number(it.sort_order) || 0) === idx * 10 ? Promise.resolve() : ref.updateOption(it.id, { sort_order: idx * 10 })));
      fetchOptions();
    } catch { toast.error("فشل إعادة الترتيب"); }
  };
  const moveGroup = (name, dir) => {
    const secs = [...sections];
    const i = secs.findIndex(s => s.name === name);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= secs.length) return;
    [secs[i], secs[j]] = [secs[j], secs[i]];
    renumber(secs.flatMap(s => s.items));
  };
  const moveItem = (item, dir) => {
    const secs = sections.map(s => ({ ...s, items: [...s.items] }));
    const sec = secs.find(s => s.items.some(it => it.id === item.id));
    const i = sec.items.findIndex(it => it.id === item.id);
    const j = i + dir;
    if (j < 0 || j >= sec.items.length) return;
    [sec.items[i], sec.items[j]] = [sec.items[j], sec.items[i]];
    renumber(secs.flatMap(s => s.items));
  };

  return (
    <div dir="rtl" style={{ padding: 20, maxWidth: 1100 }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>⚙️ الإعدادات</h1>
      
      {/* Main Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, borderBottom: "2px solid #e2e8f0", paddingBottom: 8 }}>
        <button
          onClick={() => setMainTab("options")}
          style={{
            padding: "10px 20px",
            background: mainTab === "options" ? "#2563eb" : "transparent",
            color: mainTab === "options" ? "#fff" : "#64748b",
            border: "none",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6
          }}
        >
          <FiEdit2 size={14} /> إعدادات القوائم
        </button>
        <button
          onClick={() => setMainTab("templates")}
          style={{
            padding: "10px 20px",
            background: mainTab === "templates" ? "#2563eb" : "transparent",
            color: mainTab === "templates" ? "#fff" : "#64748b",
            border: "none",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6
          }}
        >
          <FiFileText size={14} /> قوالب العقود
        </button>
        <button
          onClick={() => setMainTab("msgTemplates")}
          style={{
            padding: "10px 20px",
            background: mainTab === "msgTemplates" ? "#2563eb" : "transparent",
            color: mainTab === "msgTemplates" ? "#fff" : "#64748b",
            border: "none",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6
          }}
        >
          <FiMessageSquare size={14} /> قوالب الرسائل
        </button>
        {/* v3.3.970 — تبويب ربط واتساب انتقل إلى القائمة الجانبية (/whatsapp-settings) */}
        {/* v3.3.718 — إدارة أصناف وخيارات سجل زوار المكتب */}
        <button
          onClick={() => setMainTab("visitors")}
          style={{
            padding: "10px 20px",
            background: mainTab === "visitors" ? "#7c3aed" : "transparent",
            color: mainTab === "visitors" ? "#fff" : "#64748b",
            border: "none",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6
          }}
        >
          🚪 زوار المكتب
        </button>
      </div>

      {mainTab === "visitors" ? (
        <VisitorTypesTab />
      ) : mainTab === "templates" ? (
        <ContractTemplatesTab />
      ) : mainTab === "msgTemplates" ? (
        <MessageTemplatesTab />
      ) : (
        <>
          <p style={{ fontSize: 12, color: "#64748b", marginBottom: 20 }}>أضف/عدّل الخيارات. القوائم المعلّمة بـ 🎨 تدعم الألوان (تظهر كخلفية في الجدول)</p>

          <div style={{ display: "flex", gap: 20 }}>
            <div style={{ width: 210, flexShrink: 0 }}>
              {CATEGORIES.map(cat => cat.header ? (
                <div key={cat.key} style={{
                  padding: "8px 12px 4px", marginTop: 8, marginBottom: 2,
                  fontSize: 10, fontWeight: 800, color: "#94a3b8",
                  letterSpacing: 1, textTransform: "uppercase",
                  borderBottom: "1px solid #e2e8f0"
                }}>
                  {cat.label}
                </div>
              ) : (
                <button key={cat.key} onClick={() => setActiveCat(cat.key)} style={{
                  display: "block", width: "100%", padding: "9px 12px", marginBottom: 1,
                  border: "none", borderRadius: 6, textAlign: "right",
                  background: activeCat === cat.key ? "#eff6ff" : "transparent",
                  color: activeCat === cat.key ? "#2563eb" : "#475569",
                  fontFamily: "inherit", fontSize: 12, fontWeight: activeCat === cat.key ? 600 : 400, cursor: "pointer",
                }}>
                  {cat.hasColor && "🎨 "}{cat.label}
                  <span style={{ color: "#94a3b8", fontSize: 10 }}> ({(allOptions[cat.key] || []).length})</span>
                </button>
              ))}
            </div>

            <div style={{ flex: 1 }}>
              {/* Add form */}
              <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "flex-end" }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, color: "#64748b" }}>الخيار</label>
                  <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="أضف خيار جديد..."
                    onKeyDown={e => { if (e.key === "Enter") handleAdd(); }}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 13, marginTop: 2 }} />
                </div>
                {hasSplit && <SplitInputs sp={newSplit} setSp={setNewSplit} />}{/* v3.4.127 */}
                {/* v3.3.784 — المجموعة: كل مجموعة تظهر بوكسًا مستقلًا في فورم البوابة */}
                {hasGroup && (
                  <div style={{ width: 180 }}>
                    <label style={{ fontSize: 10, color: "#64748b" }}>المجموعة (بوكس بالفورم)</label>
                    <input value={newGroup} onChange={e => setNewGroup(e.target.value)} placeholder="مثال: عدد الغرف"
                      list="portal-groups-list"
                      onKeyDown={e => { if (e.key === "Enter") handleAdd(); }}
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 13, marginTop: 2, boxSizing: "border-box" }} />
                    <datalist id="portal-groups-list">
                      {[...new Set(items.map(x => x.group_name).filter(Boolean))].map(g => <option key={g} value={g} />)}
                    </datalist>
                  </div>
                )}
                {hasColor && (
                  <div>
                    <label style={{ fontSize: 10, color: "#64748b" }}>اللون</label>
                    <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
                      <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)}
                        style={{ width: 36, height: 34, border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer", padding: 2 }} />
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {PRESET_COLORS.slice(0, 6).map(c => (
                          <div key={c} onClick={() => setNewColor(c)} style={{
                            width: 16, height: 16, borderRadius: 3, background: c, cursor: "pointer",
                            border: newColor === c ? "2px solid #1e293b" : "1px solid #e2e8f0",
                          }} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <button onClick={handleAdd} style={{
                  display: "flex", alignItems: "center", gap: 4, padding: "8px 14px",
                  background: "#2563eb", color: "#fff", border: "none", borderRadius: 6,
                  fontFamily: "inherit", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap",
                }}><FiPlus size={14} /> إضافة</button>
              </div>

              {/* Options list — v3.3.787: أقسام بمجموعات + أسهم ترتيب */}
              <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                {items.length === 0 ? (
                  <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>لا توجد خيارات</div>
                ) : sections.map((sec, secIdx) => (
                  <div key={sec.name || "_flat"}>
                    {/* رأس المجموعة مع أسهم تقديم/تأخير المجموعة كاملة */}
                    {hasGroup && sec.name && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px",
                        background: "#f0fdfa", borderBottom: "1px solid #ccfbf1", borderTop: secIdx > 0 ? "1px solid #e2e8f0" : "none" }}>
                        <span style={{ fontSize: 12, fontWeight: 800, color: "#0f766e" }}>📦 {sec.name}</span>
                        <span style={{ fontSize: 10, color: "#94a3b8" }}>({sec.items.length})</span>
                        <span style={{ flex: 1 }} />
                        <button onClick={() => moveGroup(sec.name, -1)} disabled={secIdx === 0} title="تقديم المجموعة"
                          style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 5, padding: "1px 8px", cursor: secIdx === 0 ? "default" : "pointer", fontSize: 12, opacity: secIdx === 0 ? 0.3 : 1 }}>↑</button>
                        <button onClick={() => moveGroup(sec.name, 1)} disabled={secIdx === sections.length - 1} title="تأخير المجموعة"
                          style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 5, padding: "1px 8px", cursor: secIdx === sections.length - 1 ? "default" : "pointer", fontSize: 12, opacity: secIdx === sections.length - 1 ? 0.3 : 1 }}>↓</button>
                      </div>
                    )}
                    {sec.items.map(item => (
                  <div key={item.id} style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                    borderBottom: "1px solid #f1f5f9", opacity: item.is_active ? 1 : 0.5,
                  }}>
                    {/* Color swatch */}
                    {hasColor && (
                      editingId === item.id ? (
                        <input type="color" value={editColor} onChange={e => setEditColor(e.target.value)}
                          style={{ width: 28, height: 28, border: "1px solid #e2e8f0", borderRadius: 4, cursor: "pointer", padding: 1 }} />
                      ) : (
                        <div style={{
                          width: 24, height: 24, borderRadius: 4,
                          background: item.color || "#e2e8f0",
                          border: "1px solid rgba(0,0,0,0.1)",
                          cursor: "pointer", flexShrink: 0,
                        }} title={item.color || "بدون لون"}
                        onClick={() => {
                          const next = PRESET_COLORS[(PRESET_COLORS.indexOf(item.color) + 1) % PRESET_COLORS.length];
                          handleColorChange(item.id, next);
                        }} />
                      )
                    )}

                    {editingId === item.id ? (
                      <>
                        <input value={editLabel} onChange={e => setEditLabel(e.target.value)} autoFocus
                          onKeyDown={e => { if (e.key === "Enter") handleUpdate(item.id); }}
                          style={{ flex: 1, padding: "4px 8px", border: "1px solid #93c5fd", borderRadius: 4, fontFamily: "inherit", fontSize: 13 }} />
                        {hasSplit && <SplitInputs sp={editSplit} setSp={setEditSplit} />}{/* v3.4.127 */}
                        {/* v3.3.784 — تعديل المجموعة */}
                        {hasGroup && (
                          <input value={editGroup} onChange={e => setEditGroup(e.target.value)} placeholder="المجموعة"
                            list="portal-groups-list"
                            style={{ width: 140, padding: "4px 8px", border: "1px solid #93c5fd", borderRadius: 4, fontFamily: "inherit", fontSize: 12 }} />
                        )}
                        <button onClick={() => handleUpdate(item.id)} style={{ background: "none", border: "none", color: "#22c55e", cursor: "pointer" }}><FiCheck size={16} /></button>
                        <button onClick={() => setEditingId(null)} style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer" }}><FiX size={16} /></button>
                      </>
                    ) : (
                      <>
                        {hasColor && item.color && <span style={{
                          display: "inline-block", padding: "2px 8px", borderRadius: 4,
                          background: item.color, color: "#fff", fontSize: 11, fontWeight: 600,
                        }}>{item.label}</span>}
                        {(!hasColor || !item.color) && <span style={{ flex: 1, fontSize: 13 }}>{item.label}{hasSplit && item.group_name && <span style={{ marginInlineStart: 8, fontSize: 11, color: "#64748b", direction: "ltr", display: "inline-block" }}>({item.group_name})</span>}</span>}{/* v3.4.127 */}
                        {hasColor && item.color && <span style={{ flex: 1 }} />}
                        {/* v3.3.787 — أسهم ترتيب الخيار داخل مجموعته */}
                        {hasGroup && (
                          <>
                            <button onClick={() => moveItem(item, -1)} title="تقديم"
                              style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 5, padding: "0 7px", cursor: "pointer", fontSize: 11, color: "#64748b" }}>↑</button>
                            <button onClick={() => moveItem(item, 1)} title="تأخير"
                              style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 5, padding: "0 7px", cursor: "pointer", fontSize: 11, color: "#64748b" }}>↓</button>
                          </>
                        )}
                        <button onClick={() => handleToggle(item)} style={{
                          padding: "2px 8px", borderRadius: 10, border: "none", fontSize: 10, cursor: "pointer",
                          background: item.is_active ? "#dcfce7" : "#fee2e2", color: item.is_active ? "#166534" : "#991b1b",
                        }}>{item.is_active ? "فعّال" : "معطّل"}</button>
                        <button onClick={() => { setEditingId(item.id); setEditLabel(item.label); setEditColor(item.color || "#3b82f6"); setEditGroup(item.group_name || ""); setEditSplit(specToSplit(item.group_name)); }}
                          style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer" }}><FiEdit2 size={14} /></button>
                        <button onClick={() => handleDelete(item.id)}
                          style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer" }}><FiTrash2 size={14} /></button>
                      </>
                    )}
                  </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// تاب قوالب العقود - محرر غني مع معاينة A4
// ══════════════════════════════════════════════════════════════
function ContractTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState("");
  const [showVariables, setShowVariables] = useState(true);
  const [viewMode, setViewMode] = useState("edit"); // edit | preview
  const editorRef = React.useRef(null);

  // إعدادات الصفحة
  const [pageSettings, setPageSettings] = useState({
    marginTop: 40,
    marginBottom: 40,
    marginLeft: 40,
    marginRight: 40,
    fontSize: 14,
    lineHeight: 1.8,
    fontFamily: "Traditional Arabic"
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/contract-templates`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
        if (!data.templates || data.templates.length === 0) {
          await createDefaultTemplate();
        }
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const createDefaultTemplate = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/contract-templates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: "عقد بيع شقة",
          type: "unit_sale",
          content: DEFAULT_CONTRACT_TEMPLATE_HTML
        })
      });
      if (res.ok) {
        fetchTemplates();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setContent(template.content || "");
    // تحميل إعدادات الصفحة إذا موجودة
    if (template.variables?.pageSettings) {
      setPageSettings(template.variables.pageSettings);
    }
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/contract-templates/${selectedTemplate.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          content,
          variables: { pageSettings }
        })
      });
      if (res.ok) {
        toast.success("تم حفظ القالب");
        fetchTemplates();
      } else {
        toast.error("فشل الحفظ");
      }
    } catch (err) {
      toast.error("فشل الحفظ");
    }
    setSaving(false);
  };

  // تنفيذ أوامر التنسيق
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    // تحديث المحتوى
    if (editorRef.current) {
      setContent(editorRef.current.innerHTML);
    }
  };

  const insertVariable = (varKey) => {
    // إدراج المتغير كعنصر قابل للتمييز
    const varSpan = `<span class="template-variable" style="background:#dbeafe;color:#2563eb;padding:2px 6px;border-radius:4px;font-family:monospace;">${varKey}</span>`;
    document.execCommand('insertHTML', false, varSpan);
    if (editorRef.current) {
      setContent(editorRef.current.innerHTML);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>جاري التحميل...</div>;
  }

  const FONTS = [
    { value: "Traditional Arabic", label: "Traditional Arabic" },
    { value: "Arial", label: "Arial" },
    { value: "Tahoma", label: "Tahoma" },
    { value: "Times New Roman", label: "Times New Roman" },
    { value: "Simplified Arabic", label: "Simplified Arabic" },
    { value: "Sakkal Majalla", label: "Sakkal Majalla" },
  ];

  const FONT_SIZES = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36];

  return (
    <div style={{ display: "flex", gap: 16, height: "calc(100vh - 200px)", minHeight: 600 }}>
      {/* قائمة القوالب */}
      <div style={{ width: 180, flexShrink: 0 }}>
        <h3 style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", marginBottom: 10 }}>📄 القوالب</h3>
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
          {templates.map(t => (
            <button
              key={t.id}
              onClick={() => handleSelectTemplate(t)}
              style={{
                display: "flex", alignItems: "center", gap: 6, width: "100%",
                padding: "10px 12px", border: "none", borderBottom: "1px solid #f1f5f9",
                background: selectedTemplate?.id === t.id ? "#eff6ff" : "#fff",
                color: selectedTemplate?.id === t.id ? "#2563eb" : "#475569",
                fontFamily: "inherit", fontSize: 11, fontWeight: selectedTemplate?.id === t.id ? 600 : 400,
                cursor: "pointer", textAlign: "right"
              }}
            >
              <FiFileText size={12} /> {t.name}
            </button>
          ))}
        </div>
      </div>

      {/* منطقة المحرر */}
      {selectedTemplate ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          {/* شريط الأدوات */}
          <div style={{ 
            background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px 8px 0 0",
            padding: "8px 12px", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center"
          }}>
            {/* اختيار الخط */}
            <select 
              onChange={e => execCommand('fontName', e.target.value)}
              style={{ padding: "4px 8px", border: "1px solid #e2e8f0", borderRadius: 4, fontSize: 11 }}
            >
              {FONTS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>

            {/* حجم الخط */}
            <select 
              onChange={e => {
                const size = e.target.value;
                // execCommand fontSize يستخدم 1-7، لذا نستخدم طريقة أخرى
                const selection = window.getSelection();
                if (selection.rangeCount > 0) {
                  execCommand('fontSize', '7');
                  const fontElements = editorRef.current?.querySelectorAll('font[size="7"]');
                  fontElements?.forEach(el => {
                    el.removeAttribute('size');
                    el.style.fontSize = size + 'px';
                  });
                }
              }}
              style={{ padding: "4px 8px", border: "1px solid #e2e8f0", borderRadius: 4, fontSize: 11, width: 60 }}
            >
              {FONT_SIZES.map(s => <option key={s} value={s}>{s}px</option>)}
            </select>

            <div style={{ width: 1, height: 20, background: "#e2e8f0" }} />

            {/* أزرار التنسيق */}
            <button onClick={() => execCommand('bold')} title="عريض" style={toolbarBtnStyle}><b>B</b></button>
            <button onClick={() => execCommand('italic')} title="مائل" style={toolbarBtnStyle}><i>I</i></button>
            <button onClick={() => execCommand('underline')} title="تسطير" style={toolbarBtnStyle}><u>U</u></button>
            <button onClick={() => execCommand('strikeThrough')} title="يتوسطه خط" style={toolbarBtnStyle}><s>S</s></button>

            <div style={{ width: 1, height: 20, background: "#e2e8f0" }} />

            {/* لون النص */}
            <div style={{ position: "relative" }}>
              <input 
                type="color" 
                onChange={e => execCommand('foreColor', e.target.value)}
                title="لون النص"
                style={{ width: 28, height: 24, border: "1px solid #e2e8f0", borderRadius: 4, cursor: "pointer" }}
              />
            </div>

            {/* لون الخلفية */}
            <div style={{ position: "relative" }}>
              <input 
                type="color" 
                defaultValue="#ffff00"
                onChange={e => execCommand('hiliteColor', e.target.value)}
                title="تظليل"
                style={{ width: 28, height: 24, border: "1px solid #e2e8f0", borderRadius: 4, cursor: "pointer" }}
              />
            </div>

            <div style={{ width: 1, height: 20, background: "#e2e8f0" }} />

            {/* المحاذاة */}
            <button onClick={() => execCommand('justifyRight')} title="يمين" style={toolbarBtnStyle}>⫷</button>
            <button onClick={() => execCommand('justifyCenter')} title="وسط" style={toolbarBtnStyle}>≡</button>
            <button onClick={() => execCommand('justifyLeft')} title="يسار" style={toolbarBtnStyle}>⫸</button>
            <button onClick={() => execCommand('justifyFull')} title="ضبط" style={toolbarBtnStyle}>☰</button>

            <div style={{ width: 1, height: 20, background: "#e2e8f0" }} />

            {/* القوائم */}
            <button onClick={() => execCommand('insertUnorderedList')} title="قائمة نقطية" style={toolbarBtnStyle}>•</button>
            <button onClick={() => execCommand('insertOrderedList')} title="قائمة رقمية" style={toolbarBtnStyle}>1.</button>

            <div style={{ width: 1, height: 20, background: "#e2e8f0" }} />

            {/* المسافة البادئة */}
            <button onClick={() => execCommand('indent')} title="زيادة المسافة" style={toolbarBtnStyle}>→</button>
            <button onClick={() => execCommand('outdent')} title="تقليل المسافة" style={toolbarBtnStyle}>←</button>

            <div style={{ flex: 1 }} />

            {/* أزرار التبديل */}
            <button
              onClick={() => setViewMode(viewMode === "edit" ? "preview" : "edit")}
              style={{
                padding: "6px 12px", background: viewMode === "preview" ? "#2563eb" : "#f1f5f9",
                color: viewMode === "preview" ? "#fff" : "#64748b", border: "none", borderRadius: 4,
                fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", gap: 4
              }}
            >
              <FiEye size={12} /> {viewMode === "preview" ? "تحرير" : "معاينة A4"}
            </button>

            <button
              onClick={() => setShowVariables(!showVariables)}
              style={{
                padding: "6px 12px", background: showVariables ? "#dbeafe" : "#f1f5f9",
                color: showVariables ? "#2563eb" : "#64748b", border: "none", borderRadius: 4,
                fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", gap: 4
              }}
            >
              <FiCode size={12} /> المتغيرات
            </button>

            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: "6px 14px", background: "#22c55e", color: "#fff", border: "none",
                borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: saving ? "wait" : "pointer",
                display: "flex", alignItems: "center", gap: 4
              }}
            >
              <FiSave size={12} /> {saving ? "..." : "حفظ"}
            </button>
          </div>

          {/* منطقة التحرير والمعاينة */}
          <div style={{ flex: 1, display: "flex", gap: 12, overflow: "hidden" }}>
            {/* المحرر أو المعاينة */}
            <div style={{ 
              flex: 1, 
              overflow: "auto", 
              background: viewMode === "preview" ? "#e5e7eb" : "#fff",
              border: "1px solid #e2e8f0",
              borderTop: "none",
              borderRadius: "0 0 8px 8px",
              padding: viewMode === "preview" ? 20 : 0,
              display: "flex",
              justifyContent: "center"
            }}>
              {viewMode === "preview" ? (
                // معاينة A4
                <div style={{
                  width: "210mm",
                  minHeight: "297mm",
                  background: "#fff",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                  padding: `${pageSettings.marginTop}mm ${pageSettings.marginRight}mm ${pageSettings.marginBottom}mm ${pageSettings.marginLeft}mm`,
                  fontFamily: pageSettings.fontFamily,
                  fontSize: pageSettings.fontSize,
                  lineHeight: pageSettings.lineHeight,
                  direction: "rtl"
                }}>
                  <div dangerouslySetInnerHTML={{ __html: content }} />
                </div>
              ) : (
                // المحرر
                <div
                  ref={editorRef}
                  contentEditable
                  dir="rtl"
                  onInput={e => setContent(e.currentTarget.innerHTML)}
                  onBlur={e => setContent(e.currentTarget.innerHTML)}
                  dangerouslySetInnerHTML={{ __html: content }}
                  style={{
                    width: "100%",
                    minHeight: 500,
                    padding: 20,
                    outline: "none",
                    fontFamily: pageSettings.fontFamily,
                    fontSize: pageSettings.fontSize,
                    lineHeight: pageSettings.lineHeight,
                  }}
                />
              )}
            </div>

            {/* لوحة المتغيرات وإعدادات الصفحة */}
            {showVariables && (
              <div style={{ width: 240, flexShrink: 0, overflow: "auto" }}>
                {/* إعدادات الصفحة */}
                <div style={{ 
                  background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, 
                  padding: 12, marginBottom: 12 
                }}>
                  <h4 style={{ fontSize: 11, fontWeight: 700, color: "#1e293b", marginBottom: 10, display: "flex", alignItems: "center", gap: 4 }}>
                    📐 إعدادات الصفحة
                  </h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 9, color: "#64748b" }}>هامش علوي (mm)</label>
                      <input type="number" value={pageSettings.marginTop} 
                        onChange={e => setPageSettings({...pageSettings, marginTop: Number(e.target.value)})}
                        style={{ width: "100%", padding: 4, border: "1px solid #e2e8f0", borderRadius: 4, fontSize: 11 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 9, color: "#64748b" }}>هامش سفلي (mm)</label>
                      <input type="number" value={pageSettings.marginBottom}
                        onChange={e => setPageSettings({...pageSettings, marginBottom: Number(e.target.value)})}
                        style={{ width: "100%", padding: 4, border: "1px solid #e2e8f0", borderRadius: 4, fontSize: 11 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 9, color: "#64748b" }}>هامش أيمن (mm)</label>
                      <input type="number" value={pageSettings.marginRight}
                        onChange={e => setPageSettings({...pageSettings, marginRight: Number(e.target.value)})}
                        style={{ width: "100%", padding: 4, border: "1px solid #e2e8f0", borderRadius: 4, fontSize: 11 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 9, color: "#64748b" }}>هامش أيسر (mm)</label>
                      <input type="number" value={pageSettings.marginLeft}
                        onChange={e => setPageSettings({...pageSettings, marginLeft: Number(e.target.value)})}
                        style={{ width: "100%", padding: 4, border: "1px solid #e2e8f0", borderRadius: 4, fontSize: 11 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 9, color: "#64748b" }}>حجم الخط (px)</label>
                      <input type="number" value={pageSettings.fontSize}
                        onChange={e => setPageSettings({...pageSettings, fontSize: Number(e.target.value)})}
                        style={{ width: "100%", padding: 4, border: "1px solid #e2e8f0", borderRadius: 4, fontSize: 11 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 9, color: "#64748b" }}>تباعد الأسطر</label>
                      <input type="number" step="0.1" value={pageSettings.lineHeight}
                        onChange={e => setPageSettings({...pageSettings, lineHeight: Number(e.target.value)})}
                        style={{ width: "100%", padding: 4, border: "1px solid #e2e8f0", borderRadius: 4, fontSize: 11 }} />
                    </div>
                  </div>
                  <div style={{ marginTop: 10, padding: 8, background: "#fef3c7", borderRadius: 4, fontSize: 10, color: "#92400e" }}>
                    💡 تذكر الضغط على "حفظ" لحفظ التغييرات
                  </div>
                </div>

                {/* المتغيرات */}
                <div style={{ 
                  background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, 
                  padding: 10, maxHeight: 400, overflow: "auto"
                }}>
                  <h4 style={{ fontSize: 11, fontWeight: 700, color: "#1e293b", marginBottom: 8 }}>
                    🔗 المتغيرات
                  </h4>
                  {["تاريخ العقد", "بيانات العميل", "بيانات المشروع", "بيانات الوحدة", "بيانات الحجز"].map(source => {
                    const vars = CONTRACT_VARIABLES.filter(v => v.source === source);
                    if (vars.length === 0) return null;
                    return (
                      <div key={source} style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", marginBottom: 4 }}>{source}</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                          {vars.map(v => (
                            <button
                              key={v.key}
                              onClick={() => insertVariable(v.key)}
                              style={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                padding: "5px 8px", background: "#fff", border: "1px solid #e2e8f0",
                                borderRadius: 4, cursor: "pointer", textAlign: "right", fontSize: 10
                              }}
                            >
                              <span style={{ color: "#475569" }}>{v.label}</span>
                              <code style={{ fontSize: 9, background: "#dbeafe", color: "#2563eb", padding: "1px 4px", borderRadius: 3 }}>
                                {v.key}
                              </code>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ 
          flex: 1, display: "flex", flexDirection: "column", alignItems: "center", 
          justifyContent: "center", background: "#f8fafc", borderRadius: 12, color: "#94a3b8"
        }}>
          <FiFileText size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
          <div style={{ fontSize: 14 }}>اختر قالب من القائمة للتحرير</div>
        </div>
      )}
    </div>
  );
}

// ستايل أزرار شريط الأدوات
const toolbarBtnStyle = {
  padding: "4px 8px",
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 600,
  minWidth: 28,
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

// القالب الافتراضي بصيغة HTML
const DEFAULT_CONTRACT_TEMPLATE_HTML = `<div style="text-align: center; margin-bottom: 30px;">
  <h1 style="color: #1e3a5f; font-size: 24px;">عقد بيع وحدة سكنية</h1>
</div>

<p style="text-align: justify; margin-bottom: 15px;">
تم بعون الله وتوفيقه إبرام هذا العقد في يوم: <strong>{{Day}}</strong> الموافق <strong>{{Date}}</strong> بين كل من:
</p>

<p style="margin-bottom: 10px;">
<strong>الطرف الأول:</strong> شركة رئال للتطوير العقاري - بسجل تجاري رقم: 4030459357 - عنوانها: جدة - حي الواحة
</p>

<p style="margin-bottom: 20px;">
<strong>الطرف الثاني:</strong> {{Name}} - رقم الهوية: {{National_ID}} - رقم الجوال: {{Second_Mobile}}
</p>

<p style="margin-bottom: 20px;">
وقد وفر الطرف الأول شقة معلومة المواصفات والمقاييس (التفاصيل أدناه) بمدينة جدة بمخطط: درب الحرمين مخطط رقم 480 / ج / س الواقع في حي الفيحاء قطعة رقم ({{Lot#}}) واشترى الطرف الثاني وحرر بينهما هذا العقد على ما يلي:
</p>

<h3 style="color: #1e3a5f; border-bottom: 2px solid #1e3a5f; padding-bottom: 5px;">البند الأول: يعتبر التمهيد السابق جزءًا لا يتجزأ من العقد</h3>

<h3 style="color: #1e3a5f; border-bottom: 2px solid #1e3a5f; padding-bottom: 5px; margin-top: 20px;">البند الثاني: تفاصيل العقار المباع</h3>

<table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
  <tr style="background: #f0f0f0;">
    <th style="border: 1px solid #ccc; padding: 10px;">رقم الشقة</th>
    <th style="border: 1px solid #ccc; padding: 10px;">الدور</th>
    <th style="border: 1px solid #ccc; padding: 10px;">الواجهة</th>
    <th style="border: 1px solid #ccc; padding: 10px;">المساحة</th>
    <th style="border: 1px solid #ccc; padding: 10px;">عدد الغرف</th>
  </tr>
  <tr>
    <td style="border: 1px solid #ccc; padding: 10px; text-align: center;">{{FlatType#}}</td>
    <td style="border: 1px solid #ccc; padding: 10px; text-align: center;">{{Floor#}}</td>
    <td style="border: 1px solid #ccc; padding: 10px; text-align: center;">{{Front#}}</td>
    <td style="border: 1px solid #ccc; padding: 10px; text-align: center;">{{Area#}} م²</td>
    <td style="border: 1px solid #ccc; padding: 10px; text-align: center;">{{Rooms#}}</td>
  </tr>
</table>

<h3 style="color: #1e3a5f; border-bottom: 2px solid #1e3a5f; padding-bottom: 5px; margin-top: 20px;">البند الثالث: قيمة العقار</h3>

<p style="margin: 15px 0;">
اشترى وتملك الطرف الثاني الوحدة السكنية المذكورة أعلاه بمبلغ وقدره (<strong style="color: #c00000;">{{#Price}} ريال سعودي</strong>) ({{#PriceR}} فقط لا غير).
</p>

<h3 style="color: #1e3a5f; border-bottom: 2px solid #1e3a5f; padding-bottom: 5px; margin-top: 20px;">البند الرابع: الضمانات</h3>

<ul style="margin: 15px 0; padding-right: 20px;">
  <li>ضمان لمدة 15 سنة هيكل البناء</li>
  <li>ضمان لمدة 2 سنوات أعمال السباكة والكهرباء</li>
  <li>ضمان لمدة 5 سنوات المصاعد</li>
</ul>

<div style="display: flex; justify-content: space-between; margin-top: 60px; padding-top: 20px; border-top: 2px solid #1e3a5f;">
  <div style="width: 45%; text-align: center;">
    <p><strong>الطرف الأول</strong></p>
    <p>شركة رئال للتطوير العقاري</p>
    <p style="margin-top: 40px;">التوقيع: ..................</p>
  </div>
  <div style="width: 45%; text-align: center;">
    <p><strong>الطرف الثاني</strong></p>
    <p>{{Name}}</p>
    <p style="margin-top: 40px;">التوقيع: ..................</p>
  </div>
</div>`;
// ═══════════════════════════════════════════════════════════════
// MessageTemplatesTab — إدارة قوالب الرسائل الديناميكية
// ═══════════════════════════════════════════════════════════════
function MessageTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newTpl, setNewTpl] = useState({ category: "marketers", icon: "📝", label: "", text: "" });
  const [activeCategory, setActiveCategory] = useState("marketers");

  const VARIABLES = [
    { key: "{الاسم}", desc: "اسم المستلم" },
    { key: "{المشروع}", desc: "اسم المشروع" },
    { key: "{التاريخ}", desc: "تاريخ اليوم بالهجري" },
    { key: "{تاريخ_اليوم}", desc: "تاريخ اليوم (ميلادي)" },
  ];

  const ICONS = ["📝", "🏢", "📊", "💼", "📋", "🎯", "⚠️", "🙏", "👋", "✅", "💰", "📄", "🎬", "📎", "🔔", "💡", "🏗️", "📣"];

  const load = () => {
    setLoading(true);
    commApi.getAllTemplates()
      .then(r => setTemplates(r.data?.templates || []))
      .catch(() => toast.error("فشل تحميل القوالب"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = templates.filter(t => t.category === activeCategory);

  const handleAdd = async () => {
    if (!newTpl.label.trim() || !newTpl.text.trim()) return toast.error("أدخل الاسم والنص");
    try {
      await commApi.createTemplate(newTpl);
      toast.success("تمت الإضافة");
      setShowAdd(false);
      setNewTpl({ category: activeCategory, icon: "📝", label: "", text: "" });
      load();
    } catch { toast.error("فشل الإضافة"); }
  };

  const handleUpdate = async (id) => {
    try {
      await commApi.updateTemplate(id, editData);
      toast.success("تم التحديث");
      setEditId(null);
      load();
    } catch { toast.error("فشل التحديث"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("هل تريد حذف هذا القالب؟")) return;
    try {
      await commApi.deleteTemplate(id);
      toast.success("تم الحذف");
      load();
    } catch { toast.error("فشل الحذف"); }
  };

  const insertVariable = (variable, field) => {
    if (field === "new") {
      setNewTpl(prev => ({ ...prev, text: prev.text + variable }));
    } else if (field === "edit") {
      setEditData(prev => ({ ...prev, text: (prev.text || "") + variable }));
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1e293b" }}>📝 قوالب الرسائل</h2>
        <button onClick={() => { setShowAdd(true); setNewTpl({ category: activeCategory, icon: "📝", label: "", text: "" }); }}
          style={{ padding: "8px 16px", background: "#2563eb", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          <FiPlus size={14}/> إضافة قالب
        </button>
      </div>

      <p style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
        المتغيرات المتاحة: {VARIABLES.map(v => <span key={v.key} style={{ background: "#eff6ff", padding: "2px 6px", borderRadius: 4, margin: "0 3px", fontSize: 11, fontWeight: 600, color: "#2563eb" }}>{v.key}</span>)}
      </p>

      {/* Category tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[{ key: "marketers", label: "قوالب المسوقين" }, { key: "clients", label: "قوالب العملاء" }].map(cat => (
          <button key={cat.key} onClick={() => setActiveCategory(cat.key)}
            style={{ padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: activeCategory === cat.key ? 700 : 400,
              background: activeCategory === cat.key ? "#2563eb" : "#f1f5f9", color: activeCategory === cat.key ? "#fff" : "#64748b" }}>
            {cat.label} ({templates.filter(t => t.category === cat.key).length})
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>⏳ جاري التحميل...</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map(tpl => (
            <div key={tpl.id} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, background: editId === tpl.id ? "#eff6ff" : "#fff" }}>
              {editId === tpl.id ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <select value={editData.icon || tpl.icon} onChange={e => setEditData(prev => ({ ...prev, icon: e.target.value }))}
                      style={{ width: 50, padding: 4, border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 16, textAlign: "center" }}>
                      {ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                    </select>
                    <input value={editData.label ?? tpl.label} onChange={e => setEditData(prev => ({ ...prev, label: e.target.value }))}
                      style={{ flex: 1, padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, fontFamily: "inherit" }} placeholder="اسم القالب" />
                  </div>
                  <textarea value={editData.text ?? tpl.text} onChange={e => setEditData(prev => ({ ...prev, text: e.target.value }))}
                    rows={4} style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {VARIABLES.map(v => (
                      <button key={v.key} onClick={() => insertVariable(v.key, "edit")}
                        style={{ fontSize: 10, padding: "2px 8px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 4, cursor: "pointer", color: "#2563eb", fontFamily: "inherit" }}>
                        + {v.key}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleUpdate(tpl.id)} style={{ padding: "6px 14px", background: "#16a34a", border: "none", borderRadius: 6, color: "#fff", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}><FiCheck size={12}/> حفظ</button>
                    <button onClick={() => setEditId(null)} style={{ padding: "6px 14px", background: "#f1f5f9", border: "none", borderRadius: 6, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}><FiX size={12}/> إلغاء</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <span style={{ fontSize: 20 }}>{tpl.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>{tpl.label}</div>
                    <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{tpl.text}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button onClick={() => { setEditId(tpl.id); setEditData({ icon: tpl.icon, label: tpl.label, text: tpl.text }); }}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#2563eb", padding: 4 }}><FiEdit2 size={14}/></button>
                    <button onClick={() => handleDelete(tpl.id)}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", padding: 4 }}><FiTrash2 size={14}/></button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && <div style={{ textAlign: "center", padding: 20, color: "#94a3b8", fontSize: 13 }}>لا توجد قوالب لهذه الفئة</div>}
        </div>
      )}

      {/* Add template modal */}
      {showAdd && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setShowAdd(false)}>
          <div dir="rtl" onClick={e => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 14, width: 500, padding: 24, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#1e293b", marginBottom: 16 }}>📝 إضافة قالب جديد</div>
            
            <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
              <select value={newTpl.category} onChange={e => setNewTpl(prev => ({ ...prev, category: e.target.value }))}
                style={{ padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }}>
                <option value="marketers">مسوقين</option>
                <option value="clients">عملاء</option>
              </select>
              <select value={newTpl.icon} onChange={e => setNewTpl(prev => ({ ...prev, icon: e.target.value }))}
                style={{ width: 50, padding: 4, border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 16, textAlign: "center" }}>
                {ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
              </select>
              <input value={newTpl.label} onChange={e => setNewTpl(prev => ({ ...prev, label: e.target.value }))}
                style={{ flex: 1, padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, fontFamily: "inherit" }} placeholder="اسم القالب" />
            </div>

            <textarea value={newTpl.text} onChange={e => setNewTpl(prev => ({ ...prev, text: e.target.value }))}
              rows={5} placeholder="نص القالب... استخدم {الاسم} {المشروع} {التاريخ}"
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", marginBottom: 10 }} />

            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 14 }}>
              {VARIABLES.map(v => (
                <button key={v.key} onClick={() => insertVariable(v.key, "new")}
                  style={{ fontSize: 10, padding: "3px 10px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 4, cursor: "pointer", color: "#2563eb", fontFamily: "inherit" }}>
                  + {v.key} <span style={{ color: "#94a3b8", marginRight: 4 }}>({v.desc})</span>
                </button>
              ))}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleAdd} style={{ flex: 1, padding: "10px", background: "#2563eb", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>✅ إضافة</button>
              <button onClick={() => setShowAdd(false)} style={{ padding: "10px 20px", background: "#f1f5f9", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// v3.3.718 — VisitorTypesTab: إدارة أصناف زوار المكتب وخياراتها الفرعية
// ═══════════════════════════════════════════════════════════════
function VisitorTypesTab() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newType, setNewType] = useState("");
  const [newOption, setNewOption] = useState({}); // { [typeId]: text }
  const [editingType, setEditingType] = useState(null); // { id, name }
  const [editingOpt, setEditingOpt] = useState(null);   // { id, label }

  const load = async () => {
    setLoading(true);
    try {
      const res = await visitorsApi.getTypes();
      setTypes(res.data.types || []);
    } catch { toast.error("فشل تحميل الأصناف"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const addType = async () => {
    if (!newType.trim()) return;
    try { await visitorsApi.createType(newType.trim()); setNewType(""); toast.success("أُضيف الصنف"); load(); }
    catch (e) { toast.error(e.response?.data?.error || "فشل الإضافة"); }
  };
  const saveType = async () => {
    if (!editingType?.name?.trim()) return;
    try { await visitorsApi.updateType(editingType.id, editingType.name.trim()); setEditingType(null); load(); }
    catch { toast.error("فشل التعديل"); }
  };
  const removeType = async (t) => {
    if (!window.confirm(`حذف الصنف «${t.name}» وكل خياراته؟ (لن يؤثر على الزوار المسجلين سابقًا)`)) return;
    try { await visitorsApi.deleteType(t.id); toast.success("حُذف الصنف"); load(); }
    catch { toast.error("فشل الحذف"); }
  };
  const addOption = async (typeId) => {
    const text = (newOption[typeId] || "").trim();
    if (!text) return;
    try { await visitorsApi.createOption(typeId, text); setNewOption(p => ({ ...p, [typeId]: "" })); load(); }
    catch { toast.error("فشل إضافة الخيار"); }
  };
  const saveOption = async () => {
    if (!editingOpt?.label?.trim()) return;
    try { await visitorsApi.updateOption(editingOpt.id, editingOpt.label.trim()); setEditingOpt(null); load(); }
    catch { toast.error("فشل التعديل"); }
  };
  const removeOption = async (o) => {
    if (!window.confirm(`حذف الخيار «${o.label}»؟`)) return;
    try { await visitorsApi.deleteOption(o.id); load(); }
    catch { toast.error("فشل الحذف"); }
  };

  if (loading) return <div style={{ padding: 30, color: "#64748b" }}>جاري التحميل...</div>;

  return (
    <div style={{ maxWidth: 780 }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: "#1e293b" }}>🚪 أصناف زوار المكتب</h2>
      <p style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>
        أضف/عدّل الأصناف والخيارات الفرعية لكل صنف — تظهر في نموذج تسجيل الزائر بصفحة "سجل زوار المكتب"
      </p>

      {/* إضافة صنف */}
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        <input value={newType} onChange={e => setNewType(e.target.value)}
          onKeyDown={e => e.key === "Enter" && addType()}
          placeholder="اسم صنف جديد (مثال: زائر حكومي)"
          style={{ flex: 1, maxWidth: 320, padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 13, fontFamily: "inherit" }} />
        <button onClick={addType} style={{ padding: "9px 16px", background: "#7c3aed", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <FiPlus size={14} /> إضافة صنف
        </button>
      </div>

      {/* الأصناف */}
      {types.map(t => (
        <div key={t.id} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14, marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            {editingType?.id === t.id ? (
              <>
                <input value={editingType.name} onChange={e => setEditingType({ ...editingType, name: e.target.value })}
                  onKeyDown={e => { if (e.key === "Enter") saveType(); if (e.key === "Escape") setEditingType(null); }}
                  autoFocus style={{ padding: "6px 10px", border: "1px solid #7c3aed", borderRadius: 6, fontSize: 13.5, fontWeight: 700, fontFamily: "inherit" }} />
                <button onClick={saveType} style={{ background: "#059669", color: "#fff", border: "none", borderRadius: 6, padding: "6px 8px", cursor: "pointer" }}><FiCheck size={13} /></button>
                <button onClick={() => setEditingType(null)} style={{ background: "#f1f5f9", color: "#64748b", border: "none", borderRadius: 6, padding: "6px 8px", cursor: "pointer" }}><FiX size={13} /></button>
              </>
            ) : (
              <>
                <span style={{ fontSize: 14.5, fontWeight: 700, color: "#1e293b" }}>{t.name}</span>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>({t.options.length} خيار)</span>
                <button onClick={() => setEditingType({ id: t.id, name: t.name })} title="تعديل الاسم"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#2563eb", padding: 4 }}><FiEdit2 size={13} /></button>
                <button onClick={() => removeType(t)} title="حذف الصنف"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", padding: 4 }}><FiTrash2 size={13} /></button>
              </>
            )}
          </div>

          {/* خيارات الصنف */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            {t.options.length === 0 && <span style={{ fontSize: 11.5, color: "#cbd5e1" }}>لا توجد خيارات فرعية بعد</span>}
            {t.options.map(o => (
              editingOpt?.id === o.id ? (
                <span key={o.id} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <input value={editingOpt.label} onChange={e => setEditingOpt({ ...editingOpt, label: e.target.value })}
                    onKeyDown={e => { if (e.key === "Enter") saveOption(); if (e.key === "Escape") setEditingOpt(null); }}
                    autoFocus style={{ padding: "4px 8px", border: "1px solid #7c3aed", borderRadius: 6, fontSize: 12, fontFamily: "inherit", width: 140 }} />
                  <button onClick={saveOption} style={{ background: "#059669", color: "#fff", border: "none", borderRadius: 5, padding: "4px 6px", cursor: "pointer" }}><FiCheck size={11} /></button>
                  <button onClick={() => setEditingOpt(null)} style={{ background: "#f1f5f9", color: "#64748b", border: "none", borderRadius: 5, padding: "4px 6px", cursor: "pointer" }}><FiX size={11} /></button>
                </span>
              ) : (
                <span key={o.id} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#f5f3ff", border: "1px solid #ddd6fe", color: "#6d28d9", padding: "4px 10px", borderRadius: 14, fontSize: 12 }}>
                  {o.label}
                  <FiEdit2 size={11} style={{ cursor: "pointer" }} onClick={() => setEditingOpt({ id: o.id, label: o.label })} />
                  <FiX size={12} style={{ cursor: "pointer", color: "#dc2626" }} onClick={() => removeOption(o)} />
                </span>
              )
            ))}
          </div>

          {/* إضافة خيار */}
          <div style={{ display: "flex", gap: 6 }}>
            <input value={newOption[t.id] || ""} onChange={e => setNewOption(p => ({ ...p, [t.id]: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && addOption(t.id)}
              placeholder={`خيار جديد تحت «${t.name}»...`}
              style={{ flex: 1, maxWidth: 280, padding: "6px 10px", border: "1px dashed #cbd5e1", borderRadius: 7, fontSize: 12, fontFamily: "inherit" }} />
            <button onClick={() => addOption(t.id)} style={{ padding: "6px 12px", background: "#ede9fe", color: "#6d28d9", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              + إضافة خيار
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
